/**
 * Hands, steadied and kept in place between the frames the hand model reads.
 *
 * The hand model does not run on every frame when a phone cannot afford it
 * (public/js/trackmath.js createCadence), and a single frame now and then
 * misses a hand that is plainly there. Drawn raw, that is a hand skeleton that
 * flickers on and off and shivers. So each hand keeps its own smoother (the
 * same One Euro filter the body uses), and on a frame the hand model skipped,
 * or missed, the last hand is held for a moment and carried along with the
 * pose's wrist, so it moves with the arm instead of freezing in the air.
 *
 * For the overlay only: nothing is scored from the fingers.
 */
import { createLandmarkSmoother } from '../scoring.js';

export const HANDS = {
  HOLD_MS: 200,       // a hand not seen again is drawn this long, moving with its wrist
  RESET_MS: 400,      // gone longer than this, its smoother starts over
  SAME_PX: 0.15,      // a hand with no side is the same hand as before within this share of the frame
};

const gap = (a, b) => (a && b ? Math.hypot(a.x - b.x, a.y - b.y) : Infinity);

export function createHandTracker(T = HANDS) {
  const slots = new Map();   // 'left' | 'right' | 'free0' | 'free1' -> { sm, lm, at, wrist }

  /** Which slot a hand goes in: its side, or for a hand with no body to place it, the nearest free slot. */
  function slotFor(h, taken) {
    if (h.side) return h.side;
    let best = null, bd = Infinity;
    for (const [k, s] of slots) {
      if (!k.startsWith('free') || taken.has(k)) continue;
      const d = gap(s.lm?.[0], h.lm[0]);
      if (d < bd) { bd = d; best = k; }
    }
    if (best && bd < T.SAME_PX) return best;
    return ['free0', 'free1'].find((k) => !taken.has(k) && !slots.has(k)) || ['free0', 'free1'].find((k) => !taken.has(k)) || 'free0';
  }

  return {
    reset() { slots.clear(); },

    /**
     * `fresh`: [{ side, lm }] the hand model found on this frame, or null when
     * it did not run on this frame. `wrists`: the player's { left, right }
     * wrist points right now, 0..1, or null. Returns the hands to draw.
     */
    push(fresh, wrists, t) {
      const taken = new Set();
      if (Array.isArray(fresh)) {
        for (const h of fresh) {
          if (!Array.isArray(h?.lm) || h.lm.length < 21) continue;
          const k = slotFor(h, taken);
          taken.add(k);
          const s = slots.get(k) || { sm: createLandmarkSmoother() };
          if (!s.at || t - s.at > T.RESET_MS) s.sm.reset();
          s.lm = s.sm.push(h.lm, t);
          s.at = t;
          s.wrist = wrists?.[k] ? { x: wrists[k].x, y: wrists[k].y } : null;
          slots.set(k, s);
        }
      }
      const out = [];
      for (const [k, s] of slots) {
        if (t - s.at > T.RESET_MS) { slots.delete(k); continue; }
        if (t - s.at > T.HOLD_MS) continue;
        const side = k.startsWith('free') ? null : k;
        const w = side && wrists?.[side];
        if (!taken.has(k) && w && s.wrist) {
          // Held: carried by however far the wrist has moved since it was seen.
          const dx = w.x - s.wrist.x, dy = w.y - s.wrist.y;
          out.push({ side, lm: s.lm.map((p) => ({ ...p, x: p.x + dx, y: p.y + dy })), held: true });
        } else out.push({ side, lm: s.lm, held: !taken.has(k) });
      }
      return out;
    },
  };
}
